import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneNumber } from "@/lib/utils";
import {
  useDeactivateUserMutation,
  useUpdateMoverMutation,
} from "@/store/mover/apis";
import { updateSelectedMover } from "@/store/mover/slice";
import { User } from "@/types";
import { SaveIcon, TrashIcon } from "lucide-react";

import React, { useState } from "react";
import { useDispatch } from "react-redux";

const CrewMember: React.FC<Partial<User>> = ({
  id,
  name,
  phone,
  email,
  roles,
  pay_rate_hourly,
  preferred_language,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const [deactivateUserApi] = useDeactivateUserMutation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [crewData, setCrewData] = useState({
    name,
    phone,
    email,
    roles,
    preferred_language,
    pay_rate_hourly,
  });

  const handleUpdateCrewMember = async (index: number) => {
    if (isLoading) {
      return;
    }

    try {
      const updatedMover = await updateMoverApi({
        data: {
          users_attributes: [
            {
              id: index,
              name: crewData.name,
              phone: crewData.phone,
              pay_rate_hourly: crewData.pay_rate_hourly,
              preferred_language: crewData.preferred_language,
              ...(crewData.email ? { email: crewData.email } : {}),
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Crew updated successfully.",
      });
    } catch (error) {
      console.error("Error updating crew:", error);
      toast({
        title: "Error",
        description: "Failed to update crew. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deactivateAccount = async (id: number) => {
    if (isLoading) {
      return;
    }

    try {
      const updatedMover = await deactivateUserApi({
        user_id: id,
      }).unwrap();

      dispatch(
        updateSelectedMover({
          ...updatedMover,
          users: updatedMover.users.filter((user: User) => user.id !== id),
        })
      );

      toast({
        title: "Success",
        description: "Crew removed successfully.",
      });
    } catch (error) {
      console.error("Error removing crew:", error);
      toast({
        title: "Error",
        description: "Failed to remove crew. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!id) {
    return null;
  }

  return (
    <div>
      <div className="gap-4 items-center grid grid-cols-12 border border-stone-200 shadow-sm shadow-stone-300 rounded-md md:border-none md:shadow-none md:rounded-none p-4 md:p-0">
        <div className="col-span-12 md:col-span-2">
          <Input
            name={`crews[${id}].name`}
            value={crewData.name}
            placeholder="Name"
            onChange={(e) =>
              setCrewData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>
        {/* <div className="col-span-3">
          <Input
            name={`crews[${id}].email`}
            value={crewData.email}
            placeholder="Email"
            onChange={(e) =>
              setCrewData((prev) => ({ ...prev, email: e.target.value }))
            }
          />
        </div> */}
        <div className="col-span-12 md:col-span-2">
          <Input
            name={`crews[${id}].phone`}
            value={formatPhoneNumber(crewData.phone || "")}
            placeholder="Phone (for mobile app)"
            onChange={(e) =>
              setCrewData((prev) => ({
                ...prev,
                phone: formatPhoneNumber(e.target.value),
              }))
            }
          />
        </div>
        <div className="col-span-12 md:col-span-2">
          <Select
            value={crewData.preferred_language}
            onValueChange={(value) =>
              setCrewData((prev) => ({
                ...prev,
                preferred_language: value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="English">English</SelectItem>
              <SelectItem value="Spanish">Spanish</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-12 md:col-span-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
            <Input
              name={`crews[${id}].pay_rate_hourly`}
              value={crewData.pay_rate_hourly}
              placeholder="Hourly Pay Rate"
              className="pl-7"
              onChange={(e) =>
                setCrewData((prev) => ({
                  ...prev,
                  pay_rate_hourly: parseFloat(e.target.value),
                }))
              }
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-3 hidden">
          <div className="flex flex-row gap-2">
            <div className="flex items-baseline space-x-2">
              <Switch
                id="owner"
                checked={crewData.roles?.includes("owner")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "owner"]
                      : (prev.roles || []).filter((role) => role !== "owner"),
                  }))
                }
              />
              <div>
                <label htmlFor="owner" className="font-medium text-sm">
                  Owner
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Billing &amp; Payments
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="sales"
                checked={crewData.roles?.includes("sales")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "sales"]
                      : (prev.roles || []).filter((role) => role !== "sales"),
                  }))
                }
              />
              <div>
                <label htmlFor="sales" className="font-medium text-sm">
                  Sales
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="dispatcher"
                checked={crewData.roles?.includes("dispatcher")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "dispatcher"]
                      : (prev.roles || []).filter(
                          (role) => role !== "dispatcher"
                        ),
                  }))
                }
              />
              <div>
                <label htmlFor="dispatcher" className="font-medium text-sm">
                  Dispatcher
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="crew"
                checked={crewData.roles?.includes("crew")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "crew"]
                      : (prev.roles || []).filter((role) => role !== "crew"),
                  }))
                }
              />
              <div>
                <label htmlFor="crew" className="font-medium text-sm">
                  Crew
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-3 flex items-center gap-4">
          <Button
            className="flex items-center gap-1 px-0 border border-stone-400 px-2 text-black hover:bg-stone-200"
            variant="secondary"
            type="button"
            onClick={() => handleUpdateCrewMember(id)}
          >
            <SaveIcon className="w-3 h-3" />
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            className="text-red-700 border-red-700 flex items-center gap-1 hover:bg-red-600 hover:text-white"
            onClick={() => setShowCancelConfirm(true)}
          >
            <TrashIcon className="w-3 h-3" />
            Deactivate
          </Button>
        </div>
        <AlertDialog
          open={showCancelConfirm}
          onOpenChange={setShowCancelConfirm}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disable {name}'s access?</AlertDialogTitle>
              <AlertDialogDescription className="text-black">
                This will remove {name} from the list of crew members available
                for moves and disable their access to the app. You can give them
                access again at any time.
                <br />
                <br />
                <strong>Please confirm that you want to do this.</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-500 text-white hover:bg-red-500/90"
                onClick={() => deactivateAccount(id)}
              >
                Yes, deactivate {name}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default CrewMember;
